import { observable, computed, action, autorun } from 'mobx';
import objectPath from 'object-path';

import { AppManager } from 'core/appManager';
import { copy } from 'core/utils';
import { client } from 'core/client';

export class ConnectionAppManager extends AppManager {
  static registryName = 'ConnectionAppManager';
  disposers = [];

  @observable loadingMore = false;
  @observable batchSize;
  @observable initialBatchSize;
  @observable results = observable.map();
  @observable lastLoadedCount = 0;
  @observable total = 0;
  // @observable startCursor = null;
  loadMoreQuery = null;

  constructor(parent, initial = {}, options) {
    super(parent, initial, options);
    this.AppRef('arrayApp', this.constructor.ArrayApp);
    this.arrayApp = observable([]);
    this.disposers.push(
      autorun(
        () => {
          if (this.arrayApp.length !== 0) {
            this.lastLoadedCount = this.arrayApp.length;
          }
        },
        { name: 'lastCount' },
      ),
    );
    this.disposers.push(
      autorun(
        () => {
          const nodes = [];
          const addedCursorsMap = {};
          // eslint-disable-next-line
          for (const result of this.results.values()) {
            // eslint-disable-next-line
            for (const edge of result.edges) {
              const { cursor, node } = edge;
              if (!addedCursorsMap[cursor]) {
                nodes.push(node);
                // cursor can be undefined if missing in gql, in that case we don't
                // want to add it since it would make undefined the cursor a "key"
                // of all nodes and only 1 would show up
                if (cursor) addedCursorsMap[cursor] = true;
              }
            }
          }
          this.setNodes(nodes);
        },
        { name: 'ProcessConnectionResults' },
      ),
    );
  }

  @computed get totalLeft() {
    return this.totalCount - this.arrayApp.length;
  }

  @computed get loadedMore() {
    if (
      this.lastLoadedCount > this.initialBatchSize ||
      this.endCursor !== null
    ) {
      return true;
    }
    return false;
  }

  @computed get resultsArray() {
    return Array.from(this.results.values());
  }

  @computed get endCursor() {
    if (this.resultsArray.length > 0) {
      return this.resultsArray[this.resultsArray.length - 1].pageInfo.endCursor;
    }
    return null;
  }

  @computed get hasNextPage() {
    if (this.resultsArray.length > 0) {
      return this.resultsArray[this.resultsArray.length - 1].pageInfo
        .hasNextPage;
    }
    return true;
  }

  @computed get totalCount() {
    if (
      this.resultsArray.length !== 0 &&
      this.resultsArray[this.resultsArray.length - 1]
    ) {
      return this.resultsArray[this.resultsArray.length - 1].totalCount;
    }
    return 0;
  }

  @action setNodes = nodes => {
    this.arrayApp.update(nodes, true);
  };

  @computed get loading() {
    if (this.loadingMore) {
      return true;
    }
    if (this.manager) {
      return this.manager.loading;
    }
    return false;
  }

  @action Reset() {
    this.lastLoadedCount = 0;
    this.loadingMore = false;
    this.total = 0;
    this.Clear();
  }

  @action Clear() {
    this.queries.clear();
    this.results.clear();
  }

  @action loadMore = processOptions => {
    if (this.loadingMore) {
      return;
    }
    this.loadingMore = true;
    // eslint-disable-next-line new-cap
    const q = new this.loadMoreQuery(this);

    q.process(
      this.process,
      (...args) => {
        this.moreData(...args, false, processOptions);
      },
      processOptions,
    );

    q.query();
  };

  @action loadMoreFromCache = options => {
    // eslint-disable-next-line new-cap
    const cacheRehydrationQuery = new this.loadMoreQuery(this, {
      fetchPolicy: 'network-only',
    });
    cacheRehydrationQuery.process(this.process, this.moreData, options);

    const cacheResult = client.readQuery({
      query: cacheRehydrationQuery.constructor.conf.GQL,
      variables: cacheRehydrationQuery.evaluatedVariables,
    });
    const { resultPath } = cacheRehydrationQuery.constructor.conf;
    const relevantCacheData = objectPath.get(cacheResult, resultPath, null);
    if (relevantCacheData) {
      const processedCacheResult = this.process(observable(relevantCacheData));
      this.moreData(processedCacheResult, cacheRehydrationQuery, true);

      cacheRehydrationQuery.query();
    }
  };

  process = queryResult => {
    if (queryResult === null) return null;

    this.total = queryResult.totalCount;

    const { Model } = this.constructor.ArrayApp.ElementApp;
    const processedResult = copy(queryResult);
    processedResult.edges = queryResult.edges.map(edge => ({
      ...edge, // keep the edge.cursor, if available
      node: Model.Load(edge.node),
    }));
    return processedResult;
  };

  @action moreData = (result, query, isFromDirectCacheRead, processOptions) => {
    if (result === null) return;

    const isCacheHit =
      isFromDirectCacheRead ||
      query.updating ||
      query.fetchPolicy === 'cache-only';

    // check before pushing results
    const isInitialLoad = this.resultsArray.length === 0;

    this.pushResult(result, query);
    this.loadingMore = false;

    const hasData = result.edges.length > 0;
    const isNonEmptyCacheHit = isCacheHit && hasData;
    const canTriggerLoadAllFromCache = isFromDirectCacheRead || isInitialLoad;
    if (isNonEmptyCacheHit && canTriggerLoadAllFromCache) {
      this.loadMoreFromCache(processOptions);
    }
  };

  @action pushResult(result, query) {
    if (result !== null) {
      this.results.set(query.queryKey, result);
    }
  }

  @action pushResultIfFirst(result, query) {
    /* Either insert or update the first query */
    if (this.lastLoadedCount === 0 || this.results.get(query.queryKey)) {
      this.pushResult(result, query);
    }
  }
}
